import React from 'react';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { compose } from 'recompose';
import { withTranslation, WithTranslation, Trans } from 'react-i18next';
import get from 'lodash/get';

// constants
import { ApiResponse, LinkFull, MacAddress } from 'constants/api';
import BaseFormModal, { BaseFormModalProps } from './BaseFormModal';
import { Status } from 'constants/literals';

// components
import { BaseFormModalTemplate, FormModalButtons } from './BaseModalTemplate';
import Tooltip from 'components/utils/Tooltip';
import { isConfigurable } from 'utils/linksUtils';
import { Formik } from 'formik';

interface InputProps extends BaseFormModalProps {
    address: MacAddress;
    exchange: string;
    linkId: string;
    resourceStatus: string;
}

interface EnhanceProps {
    macAddressDeconfigureRequest: ApiDataBinding<ApiResponse<LinkFull>>,
    deleteMacAddressRequest: ApiDataBinding<ApiResponse<LinkFull>>,
}

type Props = EnhanceProps & InputProps & WithTranslation & BaseFormModalProps;

const enhance = compose<EnhanceProps, InputProps>(
    withApiData(
        {
            macAddressDeconfigureRequest: 'putMacAddressDeconfigure',
            deleteMacAddressRequest: 'deleteMacAddress',
        },
        ({ address, exchange, linkId }: Props) => ({
            macAddressDeconfigureRequest: {
                id: address.uuid,
                exchange: exchange,
                linkId: linkId,
            },
            deleteMacAddressRequest: {
                id: address.uuid,
                exchange: exchange,
                linkId: linkId,
            },
        })
    ),
    withTranslation()
);

class MacAddressDeleteModal extends BaseFormModal<Props> {
    componentDidUpdate(prevProps: Readonly<Props>): void {
        const { t } = this.props;
        this.handleFormSubmission(
            this.getRemoveMacAddressRequest(prevProps).request,
            this.getRemoveMacAddressRequest(this.props).request,
            {
                errorMessage: `${t('notification.deleteMacFailed')}: `,
                successMessage: t('notification.deleteMacSuccessfull'),
            }
        );
    }

    getRemoveMacAddressRequest = (componentProps: Props) => {
        const { macAddressDeconfigureRequest, deleteMacAddressRequest, address } = componentProps
        return this.isMacAddressConfigured(address) ? macAddressDeconfigureRequest : deleteMacAddressRequest
    };

    isMacAddressConfigured = (address: MacAddress) => {
        return address.status === Status.configured;
    };

    onSubmit = () => {
        const { address, exchange, linkId } = this.props;

        return this.getRemoveMacAddressRequest(this.props).perform({
            id: address.uuid,
            exchange: exchange,
            linkId: linkId,
        });
    };

    render() {
        const { address, t } = this.props;
        const macValue = get(address, 'value');
        return (
            <React.Fragment>
                <Formik
                    data-test="delete-mac-address-form"
                    ref={(formik) => (this.formik = formik)}
                    initialValues={{}}
                    onSubmit={this.onSubmit}
                >
                    {({ handleSubmit, isSubmitting }) => (
                        <BaseFormModalTemplate
                            data-test="mac-delete-modal-tmpl"
                            isOpen={this.state.isOpen}
                            toggle={isSubmitting ? undefined : this.toggle}
                            handleSubmit={handleSubmit}
                            header={t('deleteMacAddressModal.headerTitle')}
                            onClosed={this.handleModalClose}
                            body={<Trans i18nKey="deleteMacAddressModal.bodyMessage" values={{ macValue }} />}
                            footer={
                                <FormModalButtons
                                    data-test="mac-delete-modal-buttons"
                                    loading={isSubmitting}
                                    toggle={this.toggle}
                                />
                            }
                        />
                    )}
                </Formik>
                {isConfigurable(this.props.resourceStatus) && (
                    <Tooltip
                        data-test="mac-delete-btn"
                        triggerBtn={
                            <i
                                className="fas fa-trash fa-pull-right mt-1 cursor-pointer text-primary"
                                onClick={this.toggle}
                            />
                        }
                    >
                        {t('deleteMacAddressModal.headerTitle')}
                    </Tooltip>
                )}
            </React.Fragment>
        );
    }
}

export default enhance(MacAddressDeleteModal);